import uuid from 'utils/uuid';
import {
  ACTION_TYPES,
  SEND_MESSAGE_LABEL,
  CALL_API_LABEL,
  CALL_FUNCTION_LABEL,
  MAP_RESULT_LABEL,
  CONDITIONAL_STEP_LABEL,
  EVAL_NEXT_LABEL,
  STORE_DATA_LABEL,
  STORE_FILE_LABEL,
} from 'app-constants/actionTypes';
import { INPUT_TYPES } from 'app-constants/inputTypes';
import slugify from './utils';

// Mapeo tipo de acción → label del nodo. El label es la clave que el node factory
// de Rete 2 (`OUTPUT_CONFIG` en rete2/nodeFactory.js) usa para resolver los sockets
// de salida, así que DEBE coincidir exactamente con los `*_LABEL` de actionTypes.
// Un valor hardcodeado divergente (p.ej. 'Call API' vs 'Call Api') hace que
// `importV1` no cree outputs y se pierdan las conexiones del nodo.
const ACTION_TYPE_TO_LABEL = {
  [ACTION_TYPES.SEND_MESSAGE]: SEND_MESSAGE_LABEL,
  [ACTION_TYPES.CALL_API]: CALL_API_LABEL,
  [ACTION_TYPES.CALL_FUNCTION]: CALL_FUNCTION_LABEL,
  [ACTION_TYPES.MAP_RESULT]: MAP_RESULT_LABEL,
  [ACTION_TYPES.CONDITIONAL_STEP]: CONDITIONAL_STEP_LABEL,
  [ACTION_TYPES.EVAL_NEXT]: EVAL_NEXT_LABEL,
  [ACTION_TYPES.STORE_DATA]: STORE_DATA_LABEL,
  [ACTION_TYPES.STORE_FILE]: STORE_FILE_LABEL,
};

/**
 * Convierte spec_json de vuelta a rete_json
 */

// Patrón para extraer el ID del nodo desde el slug
const NODE_ID_PATTERN = /(\d+)$/;

/**
 * Extrae el ID numérico del nodo desde un slug
 * Ejemplo: "send_message11" -> "1", "call_function13" -> "3"
 */
const extractNodeIdFromSlug = (slug) => {
  const match = slug.match(NODE_ID_PATTERN);
  if (match) {
    // El ID puede tener múltiples dígitos, extraemos todo
    return match[1];
  }
  return null;
};

/**
 * Resuelve el id del nodo de una clave de transición (`slug(título) + id`).
 *
 * - Con el rete_json existente se usa el título real de cada nodo: es exacto
 *   (v1 limpia el `#N` del título, v2 no: se prueban ambas formas).
 * - Sin él, en v2 un título por defecto "Label#N" deja el id repetido al final
 *   (`send_message11` → 1, `call_api1010` → 10).
 * - En otro caso, los dígitos finales.
 */
const createNodeIdResolver = (specJson, existingReteJson = null) => {
  const byKey = {};
  Object.values(existingReteJson?.nodes || {}).forEach(({ id, data }) => {
    const title = data?.title;
    if (!title || id === undefined) return;
    byKey[slugify(`${title}${id}`)] = `${id}`;
    byKey[slugify(`${title.replace(/#\d+$/, '')}${id}`)] = `${id}`;
  });
  const isV2 = specJson?.version === '2.0';

  return (transitionKey) => {
    if (!transitionKey || typeof transitionKey !== 'string') return null;
    if (byKey[transitionKey]) return byKey[transitionKey];
    const digits = extractNodeIdFromSlug(transitionKey);
    if (isV2 && digits && digits.length % 2 === 0) {
      const half = digits.slice(0, digits.length / 2);
      if (`${half}${half}` === digits) return half;
    }
    return digits;
  };
};

/**
 * Detecta si una transición es implícita (generada automáticamente)
 */
const isImplicitTransition = (transitionKey) => (
  transitionKey.includes('map_result_implicit')
  || transitionKey.includes('conditional_step_implicit')
);

/**
 * Convierte variables de formato Jinja a formato de Rete. El editor delimita las
 * variables con espacios de no separación: `{\u00A0name\u00A0}` (el parser solo
 * reconoce ese formato).
 * {{ variables.session.test }} -> {\u00A0test\u00A0}
 * {{ variables.global.test }} -> {\u00A0global_test\u00A0}
 * {{ variables.user.name }} -> {\u00A0user_name\u00A0}
 * {{ variables.chatbot.name }} -> {\u00A0_name\u00A0}
 * El filtro `| to_json_str` se descarta (el parser lo vuelve a agregar donde aplica).
 */
const RETE_VARIABLE_PREFIX = {
  session: '', global: 'global_', user: 'user_', chatbot: '_',
};
const JINJA_VARIABLE = /\{\{\s*variables\.(session|global|user|chatbot)\.(\w+)\s*(?:\|\s*to_json_str\s*)?\}\}/g;

const convertJinjaToReteVariable = (text) => {
  if (!text || typeof text !== 'string') return text;

  return text.replace(
    JINJA_VARIABLE,
    (_, scope, name) => `{\u00A0${RETE_VARIABLE_PREFIX[scope]}${name}\u00A0}`,
  );
};

/**
 * Parsea un input type de spec a formato Rete
 * @param {Object} input - El input del spec_json
 * @param {Object} existingInput - El input existente del rete_json (opcional)
 */
// EDGE CASE E6: varType defaults por tipo (se pierde cuando el spec fue editado sin él)
const INPUT_VAR_TYPE_DEFAULTS = {
  [INPUT_TYPES.TEXT]: 'string',
  [INPUT_TYPES.PHONE]: 'string',
  [INPUT_TYPES.EMAIL]: 'string',
  [INPUT_TYPES.NUMBER]: 'number',
  [INPUT_TYPES.DATE]: 'string',
};

const MAX_INPUT_TIMEOUT = 10;

const parseInputTypeToRete = (specInput) => {
  // v1 guarda `data` como arreglo de objetos; v2 (offline) como un solo objeto
  // con los mismos campos. Se normaliza a arreglo para leer ambos formatos.
  const { data: rawData } = specInput;
  const isPlainObject = rawData && typeof rawData === 'object' && !Array.isArray(rawData);
  const input = isPlainObject ? { ...specInput, data: [rawData] } : specInput;
  const defaultVarType = INPUT_VAR_TYPE_DEFAULTS[input.type];
  // EDGE CASE F8: el front no permite timeout > 10; cuando el spec no lo trae, default = 10
  const rawTimeout = input.timeout !== undefined ? input.timeout : MAX_INPUT_TIMEOUT;
  /** @type {Record<string, any>} */
  const baseInput = {
    type: input.type,
    ...(input.varType ? { varType: input.varType } : defaultVarType && { varType: defaultVarType }),
    data: {},
    timeout: Math.min(rawTimeout, MAX_INPUT_TIMEOUT),
  };

  switch (input.type) {
    case INPUT_TYPES.TEXT:
    case INPUT_TYPES.PHONE:
    case INPUT_TYPES.EMAIL:
      if (input.data && Array.isArray(input.data)) {
        const placeholderObj = input.data.find((d) => d.placeholder);
        if (placeholderObj) {
          baseInput.data = { placeholder: placeholderObj.placeholder };
        }
      }
      break;

    case INPUT_TYPES.NUMBER:
      if (input.data && Array.isArray(input.data)) {
        const data = {};
        input.data.forEach((item) => {
          if (item.placeholder !== undefined) data.placeholder = item.placeholder;
          if (item.integer !== undefined) data.integer = item.integer;
          if (item.min !== undefined) data.min = item.min;
          if (item.max !== undefined) data.max = item.max;
        });
        baseInput.data = data;
      }
      break;

    case INPUT_TYPES.RADIO:
    case INPUT_TYPES.CHECKBOX:
      // Verificar si data es una variable (string) o un array de opciones
      if (typeof input.data === 'string') {
        // EDGE CASE F1: variable dinámica — preservar prefijo de scope (session.X, global.X)
        const variableName = input.data
          .replace(
            /\{\{\s*variables\.(session\.[\w.]+)\s*\|\s*to_json_str\s*\}\}/g,
            '$1',
          )
          .replace(
            /\{\{\s*variables\.(global\.[\w.]+)\s*\|\s*to_json_str\s*\}\}/g,
            '$1',
          )
          .replace(
            /\{\{\s*variables\.(user\.[\w.]+)\s*\|\s*to_json_str\s*\}\}/g,
            '$1',
          )
          .replace(
            /\{\{\s*variables\.(chatbot\.[\w.]+)\s*\|\s*to_json_str\s*\}\}/g,
            '$1',
          );
        baseInput.optionsVariable = variableName;
        baseInput.hasDynamicOptions = true;
        baseInput.data = {};
        baseInput.showIds = false;
        baseInput.varType = 'object';
        baseInput.hasDynamicOptions = true;
      } else if (Array.isArray(input.data)) {
        // EDGE CASE F-radio: opciones directas — clave UUID, estructura {id, name}
        const data = {};
        input.data.forEach((option) => {
          data[uuid()] = {
            id: option.id,
            name: option.label,
          };
        });

        baseInput.data = data;
        baseInput.showIds = false;
        baseInput.varType = 'object';
        baseInput.hasDynamicOptions = false;
      }
      break;

    case INPUT_TYPES.DATE:
      if (input.data && Array.isArray(input.data)) {
        const data = { format: 'dd/mm/yyyy', useDefaultDate: true };
        input.data.forEach((item) => {
          if (item.format) data.format = item.format;
          if (item.default !== undefined) {
            const { year, month, day } = item.default || {};
            if (year && month && day) {
              // EDGE CASE F2: construir ISO solo si los tres campos están presentes y válidos
              data.useDefaultDate = false;
              data.default = new Date(year, month - 1, day).toISOString();
            } else {
              // default vacío {} o parcial → usar fecha actual como placeholder seguro
              data.useDefaultDate = true;
              data.default = new Date().toISOString();
            }
          }
        });
        baseInput.data = data;
      }
      break;

    case INPUT_TYPES.MEDIA:
      if (input.data && Array.isArray(input.data)) {
        // EDGE CASE F3: inicializar con defaults para que picker/camera siempre estén presentes
        // picker:false es válido (F3) — preservado vía !== undefined; default "" para caso ausente
        const data = {
          media_type: '',
          picker: '',
          camera: '',
          geotag: input.geotag || false,
        };
        input.data.forEach((item) => {
          if (item.media_type) data.media_type = item.media_type;
          if (item.picker !== undefined) data.picker = item.picker;
          if (item.facing) data.camera = item.facing;
        });
        baseInput.data = data;
      }
      break;

    case INPUT_TYPES.BARCODE:
      if (input.data && Array.isArray(input.data)) {
        const data = {};
        input.data.forEach((item) => {
          // EDGE CASE F5: usar !== undefined para preservar response:""
          if (item.response !== undefined) data.response = item.response;
        });
        // EDGE CASE F4: geotag solo si explícitamente true en spec; no agregar false por defecto
        if (input.geotag) data.geotag = input.geotag;
        if (input.subtype) data.subtype = input.subtype;
        baseInput.data = data;
      }
      break;

    case INPUT_TYPES.SIGNATURE:
      if (input.data && Array.isArray(input.data)) {
        const data = {};
        input.data.forEach((item) => {
          if (item.caption) data.caption = item.caption;
        });
        // EDGE CASE F4: geotag solo si explícitamente true en spec
        if (input.geotag) data.geotag = input.geotag;
        baseInput.data = data;
      }
      break;

    case INPUT_TYPES.PASSWORD:
    case INPUT_TYPES.LOCATION:
      if (input.data && Array.isArray(input.data)) {
        const data = {};
        input.data.forEach((item) => {
          Object.assign(data, item);
        });
        baseInput.data = data;
      }
      break;

    default:
      break;
  }

  return baseInput;
};

/**
 * Parsea una acción send_message y extrae las propiedades del nodo
 * @param {Object} action - La acción del spec_json
 * @param {Object} existingNode - El nodo existente del rete_json (opcional)
 */
const parseSendMessageActionToProperties = (action, existingNode = null) => {
  const messages = {};
  const existingMessages = existingNode?.data?.properties?.messages || {};
  const existingMessageIds = Object.keys(existingMessages);

  if (action.messages && Array.isArray(action.messages) && action.messages.length > 0) {
    // EDGE CASE E7: messages:[] vacío → messages:{} (válido cuando solo hay media_url+input)
    action.messages.forEach((message) => {
      // EDGE CASE E3: usar uuid() en lugar de msg-${index}-${Date.now()} para evitar colisiones
      const messageId = uuid();

      if (typeof message === 'string') {
        messages[messageId] = {
          text: convertJinjaToReteVariable(message),
          data: {},
          name: '',
        };
      } else if (message && typeof message === 'object') {
        if (message.template) {
          // Mensaje con template y data
          const data = {};
          if (message.data) {
            Object.keys(message.data).forEach((key) => {
              data[`data-${key}`] = {
                name: key,
                value: convertJinjaToReteVariable(message.data[key]),
              };
            });
          }
          messages[messageId] = {
            text: convertJinjaToReteVariable(message.template),
            data,
            name: '',
          };
        } else {
          // EDGE CASE E1: objeto sin template — usar message.text si existe, o ignorar gracefully
          const fallbackText = typeof message.text === 'string' ? message.text : '';
          messages[messageId] = {
            text: convertJinjaToReteVariable(fallbackText),
            data: {},
            name: '',
          };
        }
      }
    });
  } else if (existingMessageIds.length > 0) {
    // Si no hay mensajes en el spec pero sí en el nodo existente, preservarlos
    Object.keys(existingMessages).forEach((msgId) => {
      messages[msgId] = { ...existingMessages[msgId] };
    });
  }

  // Parsear input si existe
  let input = {};
  let outputs = null;
  let outputsLabels = null;

  if (action.input) {
    input = parseInputTypeToRete(action.input);

    // Si es un radio button con opciones, preparar outputs y labels
    if ((action.input.type === INPUT_TYPES.RADIO || action.input.type === INPUT_TYPES.CHECKBOX)
      && Array.isArray(action.input.data)) {
      outputs = action.input.data.map((option) => option.id);
      outputsLabels = {};
      action.input.data.forEach((option) => {
        outputsLabels[option.id] = option.label;
      });
    }
  } else if (existingNode?.data?.properties?.input) {
    // Si no hay input en el spec pero sí en el nodo existente, preservarlo
    input = existingNode.data.properties.input;
    if (existingNode.data.outputs) {
      outputs = existingNode.data.outputs;
    }
    if (existingNode.data.outputsLabels) {
      outputsLabels = existingNode.data.outputsLabels;
    }
  }

  const properties = {
    type: ACTION_TYPES.SEND_MESSAGE,
    vars: action.vars || {},
    messages,
    input,
    mediaUrl: action.media_url || '',
    metadata: action.metadata || { media: '' },
    closeChat: action.close_chat !== undefined ? !action.close_chat : false,
    delay: action.delay || 0,
    timeout: action.timeout || 0,
    resultVariables: {},
  };

  // Agregar outputs y outputsLabels si existen
  if (outputs) {
    properties.outputs = outputs;
  }
  if (outputsLabels) {
    properties.outputsLabels = outputsLabels;
  }

  return properties;
};

/**
 * Parsea una acción call_function y extrae las propiedades del nodo
 */
const parseCallFunctionActionToProperties = (action) => {
  // Extraer el nombre de la función (puede venir como "global.test_global" o "session.test")
  const functionPath = action.eval.function || '';
  const functionName = functionPath.includes('.')
    ? functionPath.split('.')[1]
    : functionPath;

  // El editor guarda las entradas sin `| to_json_str`: el parser lo vuelve a agregar a las
  // variables object/array (addToJsonString). Si se deja, al regenerar queda duplicado.
  const input = Object.fromEntries(Object.entries(action.eval.input || {}).map(([key, value]) => [
    key,
    typeof value === 'string' ? value.replace(/\s*\|\s*to_json_str\s*(?=\}\})/g, ' ') : value,
  ]));

  return {
    type: ACTION_TYPES.CALL_FUNCTION,
    vars: action.vars || {},
    eval: {
      function: functionName,
      input,
      outputs: action.eval.outputs || [],
      schema: action.eval.schema || '',
      outputType: action.eval.outputType || 'string',
    },
    resultVariables: {},
  };
};

/**
 * Parsea una acción call_api y extrae las propiedades del nodo
 */
const parseCallApiActionToProperties = (action) => {
  // Parsear params desde JSON string a objeto
  let params = {};
  if (action.eval.params) {
    try {
      const parsedParams = typeof action.eval.params === 'string'
        ? JSON.parse(action.eval.params)
        : action.eval.params;

      // Convertir a formato de array de objetos para Rete
      params = Object.keys(parsedParams).reduce((acc, key, index) => {
        const value = parsedParams[key];
        if (Array.isArray(value)) {
          // Si el valor es array, crear múltiples entradas
          value.forEach((val, i) => {
            acc[`param-${index}-${i}`] = { name: key, value: convertJinjaToReteVariable(val) };
          });
        } else {
          acc[`param-${index}`] = { name: key, value: convertJinjaToReteVariable(value) };
        }
        return acc;
      }, {});
    } catch {
      // Si falla el parseo, dejar vacío
      params = {};
    }
  }

  // Parsear headers
  const headers = {};
  if (action.eval.headers) {
    Object.keys(action.eval.headers).forEach((key, index) => {
      headers[`header-${index}`] = {
        name: key,
        value: convertJinjaToReteVariable(action.eval.headers[key]),
      };
    });
  }

  // Parsear body
  let body = '{}';
  if (action.eval.body) {
    try {
      const parsedBody = typeof action.eval.body === 'string'
        ? JSON.parse(action.eval.body)
        : action.eval.body;
      // El parser v1 guarda el texto del editor serializado (`JSON.stringify(texto)`):
      // si al parsear queda un string, ese es el body original y se usa tal cual.
      body = typeof parsedBody === 'string' ? parsedBody : JSON.stringify(parsedBody, null, 2);
      body = convertJinjaToReteVariable(body);
    } catch {
      body = action.eval.body;
    }
  }

  // Parsear post_eval
  const postVerify = {
    schema: '',
    status: [],
    timecap: '0',
  };

  if (action.post_eval && action.post_eval.verify) {
    if (action.post_eval.verify.schema) {
      postVerify.schema = JSON.stringify(action.post_eval.verify.schema, null, 2);
    }
    if (action.post_eval.verify.status) {
      postVerify.status = action.post_eval.verify.status.map((code) => `${code} OK`);
    }
    if (action.post_eval.verify.timecap !== undefined) {
      postVerify.timecap = String(action.post_eval.verify.timecap);
    }
  }

  return {
    type: ACTION_TYPES.CALL_API,
    vars: action.vars || {},
    method: action.eval.method ? action.eval.method.toLowerCase() : 'get',
    url: convertJinjaToReteVariable(action.eval.url || ''),
    params,
    headers,
    body,
    postVerify,
    contentType: action.eval.content_type || 'application/json',
    integration: action.eval.integration || '',
    integrationService: action.eval.integration_service || '',
    timeout: String(action.eval.timeout || '0'),
  };
};

/**
 * Parsea una acción map_result y extrae las propiedades del nodo
 */
const parseMapResultActionToProperties = (action) => {
  const targets = {};

  if (action.eval && action.eval.targets && Array.isArray(action.eval.targets)) {
    action.eval.targets.forEach((target, index) => {
      const assignments = target.assign || [];
      targets[`target-${index}`] = {
        from: target.from,
        // `to` se conserva tal cual: el parser deja intacto un texto sin variables
        // del editor, así que `variables.session.x` vuelve igual. Convertirlo a
        // `{\u00A0x\u00A0}` sería ambiguo (una variable de sesión `user_id` se
        // leería como `variables.user.id`).
        assignments: assignments.map(({ map, to }) => ({ map, to })),
      };
    });
  }

  return {
    type: ACTION_TYPES.MAP_RESULT,
    vars: action.vars || {},
    targets,
    resultVariables: {},
  };
};

/**
 * Parsea una acción conditional_step y extrae las propiedades del nodo
 */
const parseConditionalStepActionToProperties = (action) => {
  const functionPath = action.eval.function || '';
  const functionName = functionPath.includes('.')
    ? functionPath.split('.')[1]
    : functionPath;

  return {
    type: ACTION_TYPES.CONDITIONAL_STEP,
    vars: action.vars || {},
    eval: {
      function: functionName,
      input: action.eval.input || {},
      outputs: action.eval.outputs || [],
    },
    resultVariables: {},
  };
};

/**
 * Parsea una acción eval_next y extrae las propiedades del nodo
 */
const parseEvalNextActionToProperties = (action) => {
  const functionPath = action.eval.function || '';
  const functionName = functionPath.includes('.')
    ? functionPath.split('.')[1]
    : functionPath;

  return {
    type: ACTION_TYPES.EVAL_NEXT,
    vars: action.vars || {},
    eval: {
      function: functionName,
      input: action.eval.input || {},
    },
    resultVariables: {},
  };
};

/**
 * Parsea una acción store_data y extrae las propiedades del nodo
 */
const parseStoreDataActionToProperties = (action) => ({
  type: ACTION_TYPES.STORE_DATA,
  eval: action.eval || {},
  resultVariables: {},
});

/**
 * Convierte una acción del spec_json a propiedades de nodo Rete
 * @param {Object} action - La acción del spec_json
 * @param {String} actionKey - La clave de la acción
 * @param {String} nodeId - El ID del nodo
 * @param {Object} existingNode - El nodo existente del rete_json (opcional)
 */
const parseActionToNodeProperties = (action, actionKey, nodeId, existingNode = null) => {
  const parsers = {
    [ACTION_TYPES.SEND_MESSAGE]: (act) => parseSendMessageActionToProperties(act, existingNode),
    [ACTION_TYPES.CALL_API]: parseCallApiActionToProperties,
    [ACTION_TYPES.CALL_FUNCTION]: parseCallFunctionActionToProperties,
    [ACTION_TYPES.MAP_RESULT]: parseMapResultActionToProperties,
    [ACTION_TYPES.CONDITIONAL_STEP]: parseConditionalStepActionToProperties,
    [ACTION_TYPES.EVAL_NEXT]: parseEvalNextActionToProperties,
    [ACTION_TYPES.STORE_DATA]: parseStoreDataActionToProperties,
    [ACTION_TYPES.STORE_FILE]: parseStoreDataActionToProperties,
  };

  const actionType = action.type;
  const parser = parsers[actionType];

  if (parser) {
    const properties = parser(action);

    // Generar título limpio: "Send Message#1" (sin incluir el ID del nodo en el nombre base)
    const baseName = ACTION_TYPE_TO_LABEL[actionType] || 'Unknown';
    properties.title = `${baseName}#${nodeId}`;

    return properties;
  }

  return {
    type: actionType,
    title: `Unknown#${nodeId}`,
  };
};

/**
 * Encuentra resultVariables desde map_result implícitos
 */
const extractResultVariablesFromImplicit = (transitions, actions, nodeId, transitionKey) => {
  const resultVariables = {};

  const implicitActionKey = `map_result_implicit${nodeId}_action`;

  Object.keys(transitions).forEach((tKey) => {
    if (!isImplicitTransition(tKey)) return;

    const actionKey = transitions[tKey].action;
    const action = actions[actionKey];

    if (!action || action.type !== 'map_result' || !action.eval || !action.eval.targets) return;

    // EDGE CASE F6: match primario por 'from' que referencia este transitionKey
    // Más confiable que nodeId cuando el slug codifica seqNum+nodeId juntos (A2)
    const referencesThisNode = action.eval.targets.some(
      (target) => target.from && target.from.includes(`transition.${transitionKey}.`),
    );

    if (!referencesThisNode) {
      // Fallback: matching original por transKey o action key con nodeId
      const matchesByTransKey = tKey.includes(nodeId);
      const matchesByActionKey = actionKey === implicitActionKey;
      if (!matchesByTransKey && !matchesByActionKey) return;
    }

    action.eval.targets.forEach((target) => {
      target.assign.forEach((assign) => {
        const variableId = assign.to.replace('variables.session.', '');
        // EDGE CASE F6: clave = assign.map, no variableId
        // Ej: map="selected_date", to="...date" → key="selected_date", variableId="date"
        resultVariables[assign.map] = {
          // Sin `| to_json_str`: el parser lo vuelve a agregar (mapResultFrom).
          reference: `${target.from
            .replace(/^\{\{\s*/, '')
            .replace(/\s*\}\}$/, '')
            .replace(/\s*\|\s*to_json_str\s*$/, '')}.${assign.map}`,
          type: 'string',
          variableId,
        };
      });
    });
  });

  return resultVariables;
};

/** ¿slugify(título + id) da la clave de transición? (con y sin el sufijo `#N` del título) */
const reproducesTransitionKey = (title, nodeId, transitionKey) => !!title
  && [title, title.replace(/#\d+$/, '')].some((t) => slugify(`${t}${nodeId}`) === transitionKey);

/**
 * Construye los nodos desde las transiciones y acciones del spec_json
 * @param {Object} specJson - El spec_json
 * @param {Object} existingReteJson - El rete_json existente (opcional)
 */
const buildNodesFromSpec = (specJson, existingReteJson, nodeIdOf) => {
  const { transitions, actions, entrypoint } = specJson;
  const nodes = {};
  const processedTransitions = new Set();


  // Obtener nodos existentes si existen
  const existingNodes = existingReteJson?.nodes || {};

  // Título que reproduce la clave de transición (v2: clave = slugify(título + id)).
  // 1) Si hay map_result implícito, su nombre trae el título exacto.
  // 2) Si no, la clave sin el id final, solo si ningún otro nodo deriva el mismo título
  //    (con ids ambiguos, p. ej. `send_message1223127`, varios colapsarían en "send_message").
  const IMPLICIT_PREFIX = 'map_result_implicit_';
  const strippedTitles = Object.keys(transitions)
    .filter((key) => !isImplicitTransition(key))
    .map((key) => {
      const id = nodeIdOf(key);
      return id && key.endsWith(id) ? key.slice(0, -id.length) : null;
    });
  const titleFromTransitionKey = (transitionKey, nodeId) => {
    if (specJson.version !== '2.0') return null;
    const { next } = transitions[transitionKey];
    if (typeof next === 'string' && next.startsWith(IMPLICIT_PREFIX)) {
      const implicitTitle = next.slice(IMPLICIT_PREFIX.length);
      return slugify(`${implicitTitle}${nodeId}`) === transitionKey ? implicitTitle : null;
    }
    if (!transitionKey.endsWith(nodeId)) return null;
    const stripped = transitionKey.slice(0, -nodeId.length);
    const unique = strippedTitles.filter((title) => title === stripped).length === 1;
    return stripped && unique && slugify(`${stripped}${nodeId}`) === transitionKey ? stripped : null;
  };

  // Procesar cada transición
  Object.keys(transitions).forEach((transitionKey) => {
    // Ignorar transiciones implícitas
    if (isImplicitTransition(transitionKey)) {
      return;
    }

    const transition = transitions[transitionKey];
    const actionKey = transition.action;
    const action = actions[actionKey];

    if (!action) {
      return;
    }

    // Extraer el ID del nodo desde el transitionKey
    const nodeId = nodeIdOf(transitionKey);
    if (!nodeId) {
      return;
    }

    // Si ya procesamos este nodo, saltar
    if (processedTransitions.has(nodeId)) {
      return;
    }
    processedTransitions.add(nodeId);

    // El nodo existente con el mismo id solo es "el mismo nodo" si su título reproduce esta
    // clave (misma regla que createNodeIdResolver). Al pegar en "Editar Spec JSON" el spec de
    // otro bot, los ids coinciden (101, 102…) pero los nodos no: heredar su título, input o
    // mensajes renombraba las transiciones y dejaba rotas las referencias `transition.<clave>`.
    // Sin título no hay con qué comparar: se conserva el comportamiento anterior.
    const candidate = existingNodes[nodeId];
    const existingNode = candidate
      && (!candidate.data?.title || reproducesTransitionKey(candidate.data.title, nodeId, transitionKey))
      ? candidate
      : null;

    // Parsear la acción a propiedades de nodo
    const properties = parseActionToNodeProperties(action, actionKey, nodeId, existingNode);

    // Título del nodo: preferir el del nodo existente (conserva nombres
    // personalizados y el formato "Label#id" con su casing original). Si no hay
    // nodo existente, parseActionToNodeProperties ya dejó el default
    // `${baseName}#${nodeId}`, único por nodo y que round-trip con el slug del
    // transitionKey (slugify(cleanNodeTitle(title) + nodeId)).
    // No derivar el título del slug: strip de dígitos colapsa send_message1 y
    // send_message2 en el mismo "send_message".
    if (existingNode?.data?.title) {
      properties.title = existingNode.data.title;
    } else if (slugify(`${properties.title}${nodeId}`) !== transitionKey) {
      // Spec escrito a mano / pegado en "Editar Spec JSON" (sin rete previo): el título por
      // defecto generaría otra clave (`send_message104104`) y las referencias
      // `transition.<clave>.output` de mensajes y map_result quedarían rotas.
      const derived = titleFromTransitionKey(transitionKey, nodeId);
      if (derived) properties.title = derived;
    }

    // Extraer resultVariables de map_result implícitos
    const resultVariables = extractResultVariablesFromImplicit(
      transitions, actions, nodeId, transitionKey,
    );
    if (Object.keys(resultVariables).length > 0) {
      properties.resultVariables = resultVariables;
    }

    // Determinar el nombre (label) del nodo según el tipo. Debe ser el `*_LABEL`
    // exacto: en Rete 2 es la clave de lookup del node factory para los outputs.
    const nodeName = ACTION_TYPE_TO_LABEL[properties.type] || 'Unknown';

    // EDGE CASE F-radio: derivar outputs/outputsLabels de opciones RADIO/CHECKBOX
    const inputType = properties.input && properties.input.type;
    const isRadioOrCheckbox = inputType === INPUT_TYPES.RADIO || inputType === INPUT_TYPES.CHECKBOX;
    const inputData = properties.input && properties.input.data;
    // Opciones dinámicas (data: "{{ variables.session.x | to_json_str }}"): las opciones no se
    // conocen en diseño y el nodo tiene una sola salida `nxt`. Sin esta guarda, `data: {}`
    // producía `outputs: []`, el nodo quedaba sin `nxt` y la conversión se caía.
    const hasDynamicOptions = !!properties.input?.optionsVariable;
    let nodeOutputs;
    let nodeOutputsLabels;
    if (isRadioOrCheckbox && !hasDynamicOptions
      && inputData && typeof inputData === 'object' && !Array.isArray(inputData)) {
      nodeOutputs = [];
      nodeOutputsLabels = {};
      Object.values(inputData).forEach(({ id, name }) => {
        if (id) {
          nodeOutputs.push(id);
          nodeOutputsLabels[id] = name;
        }
      });
    }

    // eval_next / conditional_step: una salida por clave de `next`. En Rete 2 el
    // node factory crea esas salidas desde `data.outputs`; sin ellas el nodo solo
    // tiene `nxt` y las conexiones a `a`, `b`… no se pueden cargar en el editor.
    const isMultiOutput = properties.type === ACTION_TYPES.EVAL_NEXT
      || properties.type === ACTION_TYPES.CONDITIONAL_STEP;
    const { next } = transition;
    if (isMultiOutput && next && typeof next === 'object') {
      nodeOutputs = Object.keys(next);
      const existingLabels = existingNode?.data?.outputsLabels || {};
      nodeOutputsLabels = Object.fromEntries(
        nodeOutputs.map((key) => [key, existingLabels[key] ?? key]),
      );
    }

    // Crear el nodo. Los outputs dinámicos (radio/checkbox) van en `data.outputs`/
    // `data.outputsLabels`: es donde el node factory de Rete 2 (`resolveOutputs`) los lee.
    nodes[nodeId] = {
      id: parseInt(nodeId, 10),
      data: {
        properties,
        title: properties.title || `${nodeName}#${nodeId}`,
        isEntryPoint: transitionKey === entrypoint,
        ...(nodeOutputs && { outputs: nodeOutputs, outputsLabels: nodeOutputsLabels }),
      },
      inputs: {
        rcv: {
          connections: [],
        },
      },
      outputs: {},
      position: [0, 0], // Se asignará después
      name: nodeName,
    };

  });

  return { nodes };
};

/**
 * Construye las conexiones (inputs/outputs) entre nodos
 */
const buildConnections = (nodes, transitions, actions, nodeIdOf) => {
  // Primero, determinar los outputs de cada nodo según su tipo
  Object.keys(nodes).forEach((nodeId) => {
    const node = nodes[nodeId];
    const { type } = node.data.properties;

    // Verificar si el nodo tiene outputs dinámicos definidos (radio buttons, checkboxes)
    if (node.data.outputs && Array.isArray(node.data.outputs)) {
      // Crear outputs dinámicos basados en las opciones
      node.outputs = {};
      node.data.outputs.forEach((outputId) => {
        node.outputs[outputId] = { connections: [] };
      });
    } else if (type === ACTION_TYPES.CONDITIONAL_STEP || type === ACTION_TYPES.EVAL_NEXT) {
      // Estos nodos tienen múltiples outputs
      node.outputs = {
        nxt: { connections: [] },
        on_error: { connections: [] },
      };
    } else if (type === ACTION_TYPES.CALL_API || type === ACTION_TYPES.CALL_FUNCTION) {
      node.outputs = {
        nxt: { connections: [] },
        on_error: { connections: [] },
      };
    } else {
      // Otros nodos solo tienen un output
      node.outputs = {
        nxt: { connections: [] },
      };
    }
  });

  // Ahora procesar las conexiones desde transitions
  Object.keys(transitions).forEach((transitionKey) => {
    if (isImplicitTransition(transitionKey)) {
      return;
    }

    const transition = transitions[transitionKey];
    const nodeId = nodeIdOf(transitionKey);

    if (!nodeId || !nodes[nodeId]) {
      return;
    }

    const node = nodes[nodeId];
    const nextTransition = transition.next;

    // Procesar las conexiones next
    if (typeof nextTransition === 'string' && nextTransition) {
      // Es una conexión simple
      let targetTransitionKey = nextTransition;

      // Si apunta a un implicit, seguir la cadena hasta encontrar el nodo real
      while (isImplicitTransition(targetTransitionKey) && transitions[targetTransitionKey]) {
        targetTransitionKey = transitions[targetTransitionKey].next;
        if (typeof targetTransitionKey === 'object') {
          break;
        }
      }

      if (typeof targetTransitionKey === 'string') {
        const targetNodeId = nodeIdOf(targetTransitionKey);
        if (targetNodeId && nodes[targetNodeId]) {
          // Agregar conexión en outputs del nodo actual
          node.outputs.nxt.connections.push({
            node: parseInt(targetNodeId, 10),
            input: 'rcv',
            data: {},
          });

          // Agregar conexión en inputs del nodo destino
          nodes[targetNodeId].inputs.rcv.connections.push({
            node: parseInt(nodeId, 10),
            output: 'nxt',
            data: {},
          });
        }
      }
    } else if (typeof nextTransition === 'object' && nextTransition) {
      // Es un objeto con múltiples salidas (conditional_step, eval_next)
      Object.keys(nextTransition).forEach((outputKey) => {
        const targetTransitionKey = nextTransition[outputKey];

        if (!targetTransitionKey) return;

        const targetNodeId = nodeIdOf(targetTransitionKey);
        if (targetNodeId && nodes[targetNodeId]) {
          // Asegurarse de que el output existe
          if (!node.outputs[outputKey]) {
            node.outputs[outputKey] = { connections: [] };
          }

          // Agregar conexión en outputs del nodo actual
          node.outputs[outputKey].connections.push({
            node: parseInt(targetNodeId, 10),
            input: 'rcv',
            data: {},
          });

          // Agregar conexión en inputs del nodo destino
          nodes[targetNodeId].inputs.rcv.connections.push({
            node: parseInt(nodeId, 10),
            output: outputKey,
            data: {},
          });
        }
      });
    }

    // Procesar on_error si existe en la acción
    const actionKey = transition.action;
    const action = actions[actionKey];
    if (action && action.on_error && action.on_error.next) {
      const errorTargetKey = action.on_error.next;
      const errorTargetNodeId = nodeIdOf(errorTargetKey);

      if (errorTargetNodeId && nodes[errorTargetNodeId]) {
        if (!node.outputs.on_error) {
          node.outputs.on_error = { connections: [] };
        }

        node.outputs.on_error.connections.push({
          node: parseInt(errorTargetNodeId, 10),
          input: 'rcv',
          data: {},
        });

        nodes[errorTargetNodeId].inputs.rcv.connections.push({
          node: parseInt(nodeId, 10),
          output: 'on_error',
          data: {},
        });
      }
    }
  });

  return nodes;
};

/**
 * Asigna posiciones a los nodos de forma automática
 * Usa un layout simple de izquierda a derecha
 */
const assignNodePositions = (nodes, entrypoint, nodeIdOf) => {
  const HORIZONTAL_SPACING = 450;
  const VERTICAL_SPACING = 150;

  const positioned = new Set();
  const levels = {};

  // Encontrar el nodo de entrada
  const entryNodeId = nodeIdOf(entrypoint);

  // BFS para asignar niveles
  const queue = [[entryNodeId, 0, 0]]; // [nodeId, level, verticalOffset]

  while (queue.length > 0) {
    const [nodeId, level, vOffset] = queue.shift();

    if (nodeId && !positioned.has(nodeId) && nodes[nodeId]) {
      positioned.add(nodeId);

      if (!levels[level]) {
        levels[level] = [];
      }
      levels[level].push({ nodeId, vOffset });

      // Agregar nodos conectados
      const node = nodes[nodeId];
      let childIndex = 0;

      Object.keys(node.outputs).forEach((outputKey) => {
        const { connections } = node.outputs[outputKey];
        connections.forEach((conn) => {
          if (!positioned.has(String(conn.node))) {
            queue.push([String(conn.node), level + 1, childIndex * VERTICAL_SPACING]);
            childIndex += 1;
          }
        });
      });
    }
  }

  // Asignar posiciones basadas en niveles
  const updatedNodes = { ...nodes };
  Object.keys(levels).forEach((level) => {
    const nodesInLevel = levels[level];
    nodesInLevel.forEach(({ nodeId, vOffset }, index) => {
      const x = parseInt(level, 10) * HORIZONTAL_SPACING + 200;
      const y = index * VERTICAL_SPACING + vOffset + 300;
      updatedNodes[nodeId] = {
        ...updatedNodes[nodeId],
        position: [x, y],
      };
    });
  });

  return updatedNodes;
};

/**
 * Crea la sección "data" del rete_json (formato legacy simplificado)
 */
const createReteDataSection = (nodes) => {
  const data = {};

  Object.keys(nodes).forEach((nodeId) => {
    const node = nodes[nodeId];
    data[nodeId] = {
      properties: node.data.properties,
      connections: {},
      ...(node.data.isEntryPoint && { isEntryPoint: true }),
    };
  });

  return data;
};

/**
 * Función principal: convierte spec_json a rete_json
 */
export const convertSpecToRete = (specJson, existingReteJson = null) => {
  // Construir nodos desde spec, preservando datos existentes
  const nodeIdOf = createNodeIdResolver(specJson, existingReteJson);
  const { nodes } = buildNodesFromSpec(specJson, existingReteJson, nodeIdOf);

  // Construir conexiones
  const nodesWithConnections = buildConnections(
    nodes,
    specJson.transitions,
    specJson.actions,
    nodeIdOf,
  );

  // Asignar posiciones
  const nodesWithPositions = assignNodePositions(
    nodesWithConnections,
    specJson.entrypoint,
    nodeIdOf,
  );

  // Crear sección data
  const data = createReteDataSection(nodesWithPositions);

  // Extraer ID del entry point
  const entryPointId = nodeIdOf(specJson.entrypoint);

  // Construir el rete_json
  const reteJson = {
    id: existingReteJson?.id || `${Date.now()}@1.0.0`,
    name: existingReteJson?.name || 'Converted Bot',
    entryPoint: entryPointId ? parseInt(entryPointId, 10) : 1,
    nodes: nodesWithPositions,
    data,
    variables: specJson.variables?.session || {},
  };

  return reteJson;
};

export default convertSpecToRete;
